// Foreground Placed Sticker Board — drag, rotate, scale, save.
// Extracted from StudentApp.jsx so it can be unit-tested in isolation.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from './studentApi';
import { STICKER_BY_ID, emojiToTwemojiUrl } from './themes';
import { Wand2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { notifyError } from '../../lib/errors';

// Single source of truth for the decoration-layer transparency so placed
// stickers always match the settings-driven background StickerLayer exactly.
export const decorStickerOpacity = (dark) => (dark ? 0.14 : 0.10);

// The floating "Customize" button belongs to the Student Desk dashboard only.
// Everywhere else in the student app (hub, badges, analytics, assignments,
// credits, settings, lesson reader, video studio …) it either has nothing to
// customize or overlaps the tutor chat panel + composer, which also live in
// the bottom-right. Allowlist, not blocklist: a new student route must opt in.
export const isCustomizableRoute = (pathname) =>
  /^\/student\/desk\/?$/.test((pathname || '').split('?')[0]);

// Default slots for stickers picked in Settings, ported from the old
// background-only StickerLayer so adopted stickers keep their exact spots.
// StickerLayer positioned by top/left(/right) corner at 80px; the board
// positions by centre at 64px * scale — hence the 1.25 scale below.
export const SETTINGS_STICKER_POSITIONS = [
  { x: 4,  y: 6  }, { x: 95, y: 14 }, { x: 8,  y: 24 }, { x: 92, y: 34 },
  { x: 3,  y: 44 }, { x: 96, y: 54 }, { x: 7,  y: 64 }, { x: 94, y: 72 },
  { x: 4,  y: 80 }, { x: 95, y: 88 }, { x: 12, y: 94 }, { x: 50, y: 38 },
];
export const SETTINGS_STICKER_SCALE = 1.25;

// Settings-picked stickers used to live in a separate, non-interactive layer,
// so the Customize controls could never touch them. Materialising them as
// ordinary board items is what makes them selectable/rotatable/removable.
// Skipped when already placed (it's the same sticker, already editable) or
// already adopted (the student has since moved or deleted it — re-adding it
// would resurrect a sticker they removed on purpose).
//
// Only 12 preset anchor spots exist (ported from the old fixed-corner
// background layer), but Settings allows up to 50 stickers. Cycling through
// the 12 anchors with a growing offset per "lap" means the 13th+ sticker
// still gets a real position instead of silently vanishing — previously
// `.slice(0, 12)` dropped anything past the 12th sticker on the floor, so a
// newly-picked Settings sticker would never show up on the Customize board.
export function materializeSettingsStickers(settingsIds = [], placed = [], adoptedIds = []) {
  const placedIds = new Set((placed || []).map(p => p.id));
  const adopted = new Set(adoptedIds || []);
  const out = [];
  const anchors = SETTINGS_STICKER_POSITIONS.length;
  (settingsIds || []).slice(0, 50).forEach((id, i) => {
    if (placedIds.has(id) || adopted.has(id)) return;
    const base = SETTINGS_STICKER_POSITIONS[i % anchors];
    const lap = Math.floor(i / anchors);
    const x = Math.max(2, Math.min(98, base.x + lap * 3));
    const y = Math.max(4, Math.min(96, base.y + lap * 5));
    out.push({ id, x, y, rotation: (i * 17) - 30, scale: SETTINGS_STICKER_SCALE });
  });
  return out;
}

// Renders a Twemoji SVG with a graceful text-glyph fallback if the CDN 404s.
export function TwemojiOrGlyph({ url, glyph, className = '', glyphClass = 'text-xl' }) {
  const [failed, setFailed] = useState(false);
  if (failed || !url) return <span className={glyphClass}>{glyph}</span>;
  return <img src={url} alt="" className={className} onError={() => setFailed(true)} />;
}

export default function PlacedStickerBoard({
  initial,
  customStickers,
  onSaved,
  dark = false,
  // Sticker ids picked in Settings, plus the ones already folded into
  // `initial` by a previous save — together they decide what gets adopted.
  settingsStickerIds = [],
  adoptedStickerIds = [],
  // Off-Desk the board is a pure decoration layer: stickers still render at
  // their saved spots, but the Customize button stays on the Desk page only.
  editable = true,
}) {
  const mergeItems = (base, settings, adopted) =>
    [...(base || []), ...materializeSettingsStickers(settings, base, adopted)];
  const [items, setItems]  = useState(() => mergeItems(initial, settingsStickerIds, adoptedStickerIds));
  const [editMode, setEdit]= useState(false);
  const [draggingId, setDraggingId] = useState(null);
  // Index of the sticker whose toolbar is pinned open (click/tap-to-select).
  // Index, not sticker id: the same sticker can be placed multiple times.
  const [selectedIdx, setSelectedIdx] = useState(null);
  // A NEW sticker being dragged out of the picker: { id, x, y, moved }.
  // x/y are viewport-% of the current pointer; `moved` distinguishes a real
  // drag (drop where released) from a plain tap (fall back to center).
  const [placing, setPlacing] = useState(null);
  const [dirty, setDirty]  = useState(false);
  const [saving, setSaving]= useState(false);
  const containerRef = useRef(null);
  const customMap = useMemo(() => customStickers.reduce((m, s) => { m[s.id] = s; return m; }, {}), [customStickers]);

  // Sync from server when initial changes (e.g., on refreshMe).
  // Keyed on CONTENT, not array identity: the id props default to fresh []
  // literals, so depending on identity re-runs this on every render — which
  // calls setItems and loops forever.
  const settingsKey = (settingsStickerIds || []).join(',');
  const adoptedKey = (adoptedStickerIds || []).join(',');
  useEffect(() => {
    setItems(mergeItems(initial, settingsStickerIds, adoptedStickerIds));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, settingsKey, adoptedKey]);

  // Navigating off the Desk mid-edit must drop back to view mode, otherwise
  // the board stays at edit z-index with no way to leave it.
  useEffect(() => {
    if (!editable) { setEdit(false); setSelectedIdx(null); }
  }, [editable]);

  // Click/tap anywhere outside a sticker (stickers stopPropagation) closes
  // the pinned toolbar. Only armed while editing.
  useEffect(() => {
    if (!editMode) return;
    const clear = () => setSelectedIdx(null);
    window.addEventListener('mousedown', clear);
    window.addEventListener('touchstart', clear);
    return () => {
      window.removeEventListener('mousedown', clear);
      window.removeEventListener('touchstart', clear);
    };
  }, [editMode]);

  // Save on blur of edit mode if dirty
  const persist = async () => {
    setSaving(true);
    try {
      // Everything the board materialised is now board-owned. Recording that
      // is what stops a removed Settings sticker from coming back on reload.
      const adopted = Array.from(new Set([
        ...(adoptedStickerIds || []),
        ...(settingsStickerIds || []).slice(0, 50),
      ]));
      await api.put('/student/profile/placed-stickers', {
        placed_stickers: items,
        adopted_sticker_ids: adopted,
      });
      toast.success('Layout saved');
      setDirty(false);
      onSaved && onSaved();
    } catch (e) { notifyError(e, 'Save failed'); }
    finally { setSaving(false); }
  };

  // Mouse / touch drag — uses % coords relative to viewport
  useEffect(() => {
    if (!draggingId) return;
    const onMove = (ev) => {
      const cl = ev.touches ? ev.touches[0] : ev;
      const w = window.innerWidth, h = window.innerHeight;
      setItems(arr => arr.map(it => it.id !== draggingId ? it : {
        ...it,
        x: Math.max(2, Math.min(98, (cl.clientX / w) * 100)),
        y: Math.max(4, Math.min(96, (cl.clientY / h) * 100)),
      }));
      setDirty(true);
    };
    const onUp = () => setDraggingId(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId]);

  // Placing a NEW sticker: follow the pointer while dragging out of the
  // picker, then drop it at the release coordinates — so it lands exactly
  // where the student lets go instead of snapping to the center.
  useEffect(() => {
    if (!placing) return;
    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      const cl = ev.touches ? ev.touches[0] : ev;
      const w = window.innerWidth, h = window.innerHeight;
      setPlacing(p => p && ({
        ...p,
        x: Math.max(2, Math.min(98, (cl.clientX / w) * 100)),
        y: Math.max(4, Math.min(96, (cl.clientY / h) * 100)),
        moved: true,
      }));
    };
    const onUp = () => {
      setPlacing(p => {
        if (p) {
          // Drag → drop at the release point; plain tap → center fallback.
          const x = p.moved ? p.x : 50;
          const y = p.moved ? p.y : 50;
          setItems(arr => arr.length >= 50 ? arr : [...arr, { id: p.id, x, y, rotation: 0, scale: 1 }]);
          setDirty(true);
        }
        return null;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [placing?.id]);

  const removeItem = (id) => { setItems(it => it.filter(x => x.id !== id)); setSelectedIdx(null); setDirty(true); };
  const rotate    = (id) => { setItems(it => it.map(x => x.id === id ? { ...x, rotation: (x.rotation || 0) + 15 } : x)); setDirty(true); };
  const scaleUp   = (id) => { setItems(it => it.map(x => x.id === id ? { ...x, scale: Math.min(3,  (x.scale || 1) + 0.2) } : x)); setDirty(true); };
  const scaleDown = (id) => { setItems(it => it.map(x => x.id === id ? { ...x, scale: Math.max(0.4,(x.scale || 1) - 0.2) } : x)); setDirty(true); };

  // Keyboard fallback (Enter/Space on a picker button can't carry pointer
  // coords) — drops the sticker at center so it's always addable, then the
  // student can drag it wherever they like.
  const addAtCenter = (stickerId) => {
    if (items.length >= 50) { toast.error('Max 50 stickers placed.'); return; }
    setItems(arr => [...arr, { id: stickerId, x: 50, y: 50, rotation: 0, scale: 1 }]);
    setDirty(true);
  };

  // Pointer-drag entry point from the picker: seed `placing` at the pointer;
  // the effect above tracks the drag and drops on release.
  const startPlacing = (stickerId, ev) => {
    if (items.length >= 50) { toast.error('Max 50 stickers placed.'); return; }
    const cl = ev.touches ? ev.touches[0] : ev;
    const w = window.innerWidth, h = window.innerHeight;
    setPlacing({
      id: stickerId,
      x: Math.max(2, Math.min(98, (cl.clientX / w) * 100)),
      y: Math.max(4, Math.min(96, (cl.clientY / h) * 100)),
      moved: false,
    });
  };

  const renderSticker = (it) => {
    const custom = customMap[it.id];
    if (custom) {
      return <img src={`${process.env.REACT_APP_BACKEND_URL}${custom.url}`} alt="" className="w-full h-full object-contain pointer-events-none" />;
    }
    const s = STICKER_BY_ID[it.id];
    if (!s) return null;
    const tw = emojiToTwemojiUrl(s.emoji);
    return tw
      ? <TwemojiOrGlyph url={tw} glyph={s.emoji} className="w-full h-full object-contain pointer-events-none" glyphClass="text-4xl pointer-events-none" />
      : <span className="text-4xl pointer-events-none">{s.emoji}</span>;
  };

  return (
    <>
      {/* Foreground placed stickers — pointer-events:none on the parent;
          individual sticker children opt-in to pointer-events:auto in edit mode.
          This guarantees the floating Customize button (z-50) always receives
          clicks even when an existing sticker overlaps the bottom-right area.
          View mode sits at z-5 (a decoration layer, below the z-10 <main>
          column); edit mode lifts to z-45 — otherwise <main> swallows every
          hover/click on stickers inside the content area and the controls
          are only reachable over the sidebar strip. */}
      <div ref={containerRef}
        className={`fixed inset-0 pointer-events-none ${editMode ? 'z-[45]' : 'z-[5]'}`}
        data-testid="placed-sticker-board">
        {items.map((it, i) => {
          const sz = 64 * (it.scale || 1);
          const selected = editMode && selectedIdx === i;
          const select = () => { setDraggingId(it.id); setSelectedIdx(i); };
          return (
            <div key={`${it.id}-${i}`}
              data-testid={`placed-${it.id}-${i}`}
              className={`absolute group ${editMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
              style={{
                left: `${it.x}%`, top: `${it.y}%`,
                width: sz, height: sz,
                transform: `translate(-50%, -50%) rotate(${it.rotation || 0}deg)`,
                // Same transparency as the settings-driven StickerLayer once
                // placed; full opacity while editing so it can be manipulated.
                opacity: editMode ? 1 : decorStickerOpacity(dark),
                transition: 'opacity 200ms',
                pointerEvents: editMode ? 'auto' : 'none',
                userSelect: 'none',
                touchAction: 'none',
                ...(selected ? { outline: '2px dashed var(--st-primary)', outlineOffset: 2, borderRadius: 8 } : {}),
              }}
              onMouseDown={(e) => { if (editMode) { e.preventDefault(); e.stopPropagation(); select(); } }}
              onTouchStart={(e) => { if (editMode) { e.stopPropagation(); select(); } }}>
              {renderSticker(it)}
              {editMode && (
                <div
                  className={`absolute -top-2 -right-2 flex gap-0.5 transition-opacity ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  // Keep the buttons upright regardless of sticker rotation,
                  // and don't let pressing them start a drag / clear selection.
                  style={{ transform: `rotate(${-(it.rotation || 0)}deg)` }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onTouchStart={(e) => e.stopPropagation()}>
                  <button onClick={(e) => { e.stopPropagation(); rotate(it.id); }} title="Rotate" className="w-5 h-5 rounded-full bg-black/70 text-white text-[10px]" data-testid={`rotate-${it.id}-${i}`}>↻</button>
                  <button onClick={(e) => { e.stopPropagation(); scaleUp(it.id); }}  title="Bigger" className="w-5 h-5 rounded-full bg-black/70 text-white text-[10px]">+</button>
                  <button onClick={(e) => { e.stopPropagation(); scaleDown(it.id); }}title="Smaller" className="w-5 h-5 rounded-full bg-black/70 text-white text-[10px]">−</button>
                  <button onClick={(e) => { e.stopPropagation(); removeItem(it.id); }} title="Remove" className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px]" data-testid={`remove-${it.id}-${i}`}>×</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ghost preview that follows the pointer while placing a new sticker */}
      {placing && (
        <div className="fixed z-[60] pointer-events-none"
          style={{ left: `${placing.x}%`, top: `${placing.y}%`, width: 64, height: 64, transform: 'translate(-50%, -50%)', opacity: 0.85 }}
          data-testid="placing-preview">
          {renderSticker({ id: placing.id })}
        </div>
      )}

      {/* Floating customize toggle (bottom-right) — Desk page only */}
      {editable && (
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        {editMode && <PlacedStickerPicker onPlaceStart={startPlacing} onAddCenter={addAtCenter} customStickers={customStickers} />}
        <button
          onClick={async () => {
            if (editMode && dirty) await persist();
            setSelectedIdx(null);
            setEdit(e => !e);
          }}
          disabled={saving}
          data-testid="placed-customize-btn"
          className="rounded-full px-4 py-2 text-xs font-bold shadow-lg flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
          style={{ background: editMode ? 'var(--st-primary)' : 'var(--st-card)', color: editMode ? '#fff' : 'inherit', border: '2px solid var(--st-border)' }}>
          {saving ? <>Saving…</> : editMode ? <><Save className="w-3.5 h-3.5" /> Done</> : <><Wand2 className="w-3.5 h-3.5" /> Customize</>}
        </button>
      </div>
      )}
    </>
  );
}

// Compact sticker picker shown above the Customize button in edit mode.
// Buttons are pointer-draggable: press one and drag onto the page to drop the
// sticker exactly where you release (see startPlacing). Keyboard activation
// (onAddCenter) drops at center as an accessible fallback.
function PlacedStickerPicker({ onPlaceStart, onAddCenter, customStickers }) {
  const [open, setOpen] = useState(false);
  const recent = [
    'smile-happy', 'smile-cool', 'h-red', 'h-pink', 'cl-party', 'cl-tada',
    'st-light', 'st-books', 'sp-football', 'mu-headphones', 'm-sparkle', 'c-fire',
  ];
  // Shared handlers so built-in + custom stickers behave identically.
  const dragProps = (id) => ({
    onMouseDown: (e) => { e.preventDefault(); onPlaceStart(id, e); },
    onTouchStart: (e) => { e.preventDefault(); onPlaceStart(id, e); },
    // Only fire on keyboard activation (detail === 0); pointer clicks are
    // already handled by the drag path, so this avoids a double-add.
    onClick: (e) => { if (e.detail === 0) onAddCenter(id); },
  });
  return (
    <div className="rounded-2xl shadow-xl border-2 p-2" style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)', maxWidth: 320 }} data-testid="placed-add-picker">
      <div className="flex items-center justify-between gap-2 px-1 pb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Drag a sticker onto the page</span>
        <button onClick={() => setOpen(o => !o)} className="text-[10px] opacity-60 hover:opacity-100">{open ? 'Less' : 'All'}</button>
      </div>
      <div className="flex flex-wrap gap-1 max-h-[160px] overflow-auto">
        {recent.filter(id => STICKER_BY_ID[id]).map((id, i) => {
          const s = STICKER_BY_ID[id];
          const tw = emojiToTwemojiUrl(s.emoji);
          return (
            <button key={`${id}-${i}`} {...dragProps(id)} title={s.label}
              data-testid={`placed-add-${id}`}
              className="w-9 h-9 rounded-lg hover:bg-black/5 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none">
              <TwemojiOrGlyph url={tw} glyph={s.emoji} className="w-7 h-7 pointer-events-none" glyphClass="text-xl" />
            </button>
          );
        })}
        {customStickers.slice(0, 12).map((s, i) => (
          <button key={`${s.id}-${i}`} {...dragProps(s.id)} title={s.label}
            data-testid={`placed-add-custom-${s.id}`}
            className="w-9 h-9 rounded-lg hover:bg-black/5 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt="" className="w-7 h-7 object-contain pointer-events-none" />
          </button>
        ))}
      </div>
      {open && (
        <div className="text-[10px] mt-2 px-1 opacity-60">
          Tip: Drag a sticker onto the page and drop it where you want it. Add more from <b>Settings → Stickers</b>.
        </div>
      )}
    </div>
  );
}
